import * as React from "react"
import { ImSpinner2 } from "react-icons/im"

import clsxm from "@/lib/clsxm"

import Icon from "@/components/common/Icon/Icon"

enum ButtonSize {
  small,
  medium,
  large,
}

type ButtonProps = {
  isLoading?: boolean
  variant?: `primary`
  buttonSize?: keyof typeof ButtonSize
  icon: React.ElementType
  ariaLabel: string
  className?: string
} & React.ComponentPropsWithRef<`button`>

const Loading = () => {
  return (
    <div
      className={clsxm(
        `absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`
      )}
    >
      <ImSpinner2 className={`animate-spin`} />
    </div>
  )
}

const IconButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      buttonSize = `medium`,
      children,
      className,
      disabled: buttonDisabled,
      variant = `primary`,
      icon,
      isLoading,
      ariaLabel,
      ...rest
    },
    ref
  ) => {
    const disabled = isLoading || buttonDisabled

    return (
      <button
        ref={ref}
        type={`button`}
        aria-label={ariaLabel}
        disabled={disabled}
        className={clsxm(
          `flex items-center justify-center rounded-full bg-primary-500 text-white`,
          `focus:outline-none focus-visible:ring focus-visible:ring-primary-500`,
          `shadow-sm`,
          `border border-primary-600`,
          `transition-colors duration-150`,
          disabled
            ? `cursor-not-allowed border-gray-500 bg-gray-500`
            : `hover:bg-primary-600 hover:text-white active:bg-primary-500`,
          [
            buttonSize === `small` && [`h-8 w-8`],
            buttonSize === `medium` && [`h-10 w-10`],
            buttonSize === `large` && [`h-14 w-14`],
          ],
          className
        )}
        {...rest}
      >
        {isLoading && <Loading />}
        <Icon
          className={clsxm(``, [
            buttonSize === `small` && [`h-4 w-4`],
            buttonSize === `medium` && [`h-6 w-6`],
            buttonSize === `large` && [`h-8 w-8`],
          ])}
          icon={icon}
        />
      </button>
    )
  }
)

export default IconButton
